import { useEffect, useRef, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import api, { formatApiError } from "@/lib/api";
import { prepareClientPhoto } from "@/lib/clientPhoto";
import { ClientAvatar } from "@/components/ClientAvatar";
import PropertyInventoryPanel from "@/components/PropertyInventoryPanel";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { ArrowLeft, ExternalLink, Phone, Mail, Camera } from "lucide-react";
import { toast } from "sonner";

const CONTACT_LABELS = { guardian: "Guardian", sc: "Support Coordinator (SC)", behaviorist: "Behaviorist" };

const computeAge = (dob) => {
  if (!dob) return null;
  const d = new Date(dob + "T00:00:00");
  if (Number.isNaN(d.getTime())) return null;
  const now = new Date();
  let age = now.getFullYear() - d.getFullYear();
  const m = now.getMonth() - d.getMonth();
  if (m < 0 || (m === 0 && now.getDate() < d.getDate())) age -= 1;
  return age >= 0 && age < 130 ? age : null;
};

export default function ProviderClientProfile() {
  const { clientId } = useParams();
  const navigate = useNavigate();
  const fileRef = useRef(null);
  const cameraRef = useRef(null);
  const [c, setC] = useState(null);
  const [goals, setGoals] = useState([]);
  const [uploading, setUploading] = useState(false);
  const [analytics, setAnalytics] = useState(null);

  useEffect(() => {
    api.get(`/clients/${clientId}`).then(({ data }) => setC(data));
    api.get(`/clients/${clientId}/goals`).then(({ data }) => setGoals(data));
    api.get(`/outcome-analytics?client_id=${clientId}`).then(({ data }) => setAnalytics(data)).catch(() => {});
  }, [clientId]);

  if (!c) return <p className="text-muted-foreground">Loading…</p>;

  const handlePhoto = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploading(true);
    try {
      // v40 — 10MB cap + auto-downscale for large phone photos. Prior
      // 1MB browser-side reject was silently blocking every real photo.
      const dataUrl = await prepareClientPhoto(file);
      const { data } = await api.post(`/clients/${clientId}/photo`, { photo_data_url: dataUrl });
      setC(data);
      toast.success("Photo updated");
    } catch (err) {
      toast.error(err?.message || formatApiError(err.response?.data?.detail) || "Upload failed");
    } finally {
      setUploading(false);
      e.target.value = "";
    }
  };

  return (
    <div className="space-y-5" data-testid="provider-client-profile">
      <Button variant="ghost" size="sm" onClick={() => navigate("/provider")} data-testid="back-to-home">
        <ArrowLeft className="w-4 h-4 mr-1" /> Back
      </Button>

      <Card className="p-5 flex items-center gap-4">
        <div className="relative">
          <ClientAvatar
            name={c.name}
            photoUrl={c.photo_data_url}
            className="h-24 w-24 rounded-full object-cover border-2 border-primary/20 shadow-sm"
            placeholderClassName="h-24 w-24 rounded-full bg-primary/10 text-brand-navy flex items-center justify-center font-display font-bold text-2xl border-2 border-primary/20"
            testId="provider-client-avatar"
          />
          <div className="grid grid-cols-2 gap-1.5 mt-2">
            <Button
              size="sm" variant="outline"
              onClick={() => fileRef.current?.click()}
              disabled={uploading}
              className="text-[10px] h-8 px-1"
              data-testid="provider-upload-photo"
            >
              <Camera className="w-3 h-3 mr-1" /> {uploading ? "…" : "Photo"}
            </Button>
            <Button
              size="sm" variant="outline"
              onClick={() => cameraRef.current?.click()}
              disabled={uploading}
              className="text-[10px] h-8 px-1"
              data-testid="provider-capture-photo"
            >
              <Camera className="w-3 h-3 mr-1" /> Camera
            </Button>
          </div>
          <input ref={fileRef} type="file" accept="image/*" onChange={handlePhoto} className="hidden" data-testid="provider-photo-input" />
          <input ref={cameraRef} type="file" accept="image/*" capture="environment" onChange={handlePhoto} className="hidden" data-testid="provider-camera-input" />
        </div>
        <div className="flex-1 min-w-0">
          <p className="text-label">Client Profile</p>
          <h1 className="font-display text-2xl font-bold tracking-tight">{c.name}</h1>
          {(() => {
            const age = computeAge(c.dob);
            return age != null ? (
              <p className="text-sm text-muted-foreground mt-0.5" data-testid="provider-client-age">{age} years old</p>
            ) : null;
          })()}
          <div className="flex flex-wrap gap-1.5 mt-1.5">
            {c.gender && <Badge variant="secondary">{c.gender}</Badge>}
          </div>
        </div>
      </Card>

      {analytics && (
        <Card className="p-4" data-testid="provider-monthly-summary">
          <div className="flex items-center justify-between gap-2 flex-wrap mb-2">
            <p className="text-label">This Month's Snapshot</p>
            <p className="text-[10px] uppercase tracking-[0.18em] text-muted-foreground">Read-only • last 30 days</p>
          </div>
          <p className="text-xs text-muted-foreground mb-3">
            {analytics.note_days_30d} of {analytics.month_days} days had a Daily Note submitted.
          </p>
          <div className="grid grid-cols-2 sm:grid-cols-5 gap-2">
            {analytics.buckets.map((b) => {
              const labels = {
                activities: "Activities", mood: "Mood", meals: "Meals",
                attendance_checks: "Attendance", pcsp_goals: "Client Goals",
              };
              return (
                <div key={b} className="rounded-md border border-border p-2.5 bg-secondary/30 text-center"
                     data-testid={`provider-snapshot-${b}`}>
                  <p className="text-[9px] uppercase tracking-[0.18em] text-muted-foreground">{labels[b]}</p>
                  <p className="font-display font-bold text-xl">{analytics.totals_30d[b]}</p>
                  <p className="text-[10px] text-muted-foreground">checkmarks</p>
                </div>
              );
            })}
          </div>
        </Card>
      )}

      <Card className="p-4">
        <p className="text-label mb-2">Client Goals</p>
        {goals.length === 0 && <p className="text-sm text-muted-foreground">No active goals.</p>}
        <ul className="space-y-2">
          {goals.map((g) => (
            <li key={g.id} className="border border-border rounded-md p-3" data-testid={`profile-goal-${g.id}`}>
              <p className="text-[10px] uppercase tracking-[0.18em] text-muted-foreground mb-0.5">Goal</p>
              <p className="font-display font-semibold text-sm">{g.title}</p>
              {g.description && (
                <>
                  <p className="text-[10px] uppercase tracking-[0.18em] text-muted-foreground mt-2 mb-0.5">Support Details</p>
                  <p className="text-xs text-muted-foreground whitespace-pre-wrap">{g.description}</p>
                </>
              )}
            </li>
          ))}
        </ul>
      </Card>

      <Card className="p-4">
        <p className="text-label mb-2">Points of Contact</p>
        {/* Family / Friends list */}
        <div className="border border-border rounded-md p-3 mb-3" data-testid="provider-family-contacts">
          <p className="text-xs uppercase tracking-[0.18em] text-muted-foreground mb-2">Family / Friends</p>
          {(Array.isArray(c.contacts?.family) ? c.contacts.family : []).length === 0 ? (
            <p className="text-sm text-muted-foreground">No contacts recorded.</p>
          ) : (
            <ul className="space-y-2">
              {c.contacts.family.map((row) => (
                <li key={row.id || row.name} className="text-sm flex flex-wrap items-center justify-between gap-2" data-testid={`provider-family-${row.id || row.name}`}>
                  <span className="font-semibold">{row.name || "—"}</span>
                  {row.relation && <span className="text-xs text-muted-foreground">{row.relation}</span>}
                  {row.phone && (
                    <span className="text-xs flex items-center gap-1">
                      <Phone className="w-3 h-3" /> {row.phone}
                    </span>
                  )}
                </li>
              ))}
            </ul>
          )}
        </div>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
          {Object.entries(CONTACT_LABELS).map(([k, label]) => {
            const v = c.contacts?.[k] || {};
            return (
              <div key={k} className="border border-border rounded-md p-3">
                <p className="text-xs uppercase tracking-[0.18em] text-muted-foreground">{label}</p>
                <p className="font-semibold text-sm mt-0.5">{v.name || "—"}</p>
                {v.relation && <p className="text-xs text-muted-foreground">{v.relation}</p>}
                {v.phone && <p className="text-xs flex items-center gap-1 mt-1"><Phone className="w-3 h-3" /> {v.phone}</p>}
                {v.email && <p className="text-xs flex items-center gap-1"><Mail className="w-3 h-3" /> {v.email}</p>}
              </div>
            );
          })}
        </div>
      </Card>

      <Card className="p-4" data-testid="provider-doc-hub">
        <p className="text-label mb-2">Document Link Hub</p>
        <div className="space-y-2">
          {/* v28 — Structured 5-slot link hub (PCSP, Support Strategy, Room & Board, BSP, Other) */}
          {[
            { key: "pcsp_url", label: "Person-Centered Service Plan (PCSP)", optional: false },
            { key: "support_strategy_url", label: "Support Strategy", optional: false },
            { key: "room_board_url", label: "Room & Board Agreement", optional: false },
            { key: "bsp_url", label: "Behavior Support Plan (BSP)", optional: true },
          ].map(({ key, label, optional }) => {
            const url = c[key];
            if (optional && !url) return null;   // BSP hides entirely when not set
            return (
              <a
                key={key}
                href={url || "#"}
                target="_blank"
                rel="noopener noreferrer"
                onClick={(e) => { if (!url) e.preventDefault(); }}
                className={`flex items-center justify-between p-3 rounded-md ${url ? "bg-secondary hover:bg-secondary/70" : "bg-secondary/30 opacity-60 cursor-not-allowed"}`}
                data-testid={`structured-link-btn-${key}`}
              >
                <span className="text-sm font-medium flex items-center gap-2">
                  {label}
                  {optional && url && <span className="text-[9px] uppercase tracking-wider bg-emerald-100 text-emerald-700 px-1.5 py-0.5 rounded font-semibold">Linked</span>}
                </span>
                {url ? <ExternalLink className="w-4 h-4 text-primary" /> : <span className="text-xs text-muted-foreground">not set</span>}
              </a>
            );
          })}
          {(c.doc_links || []).filter((d) => d.url).length > 0 && (
            <p className="text-[10px] uppercase tracking-[0.18em] font-semibold text-muted-foreground pt-2">Other Resources</p>
          )}
          {(c.doc_links || []).map((d) => (
            <a
              key={d.id}
              href={d.url || "#"}
              target="_blank"
              rel="noopener noreferrer"
              onClick={(e) => { if (!d.url) e.preventDefault(); }}
              className={`flex items-center justify-between p-3 rounded-md ${d.url ? "bg-secondary hover:bg-secondary/70" : "bg-secondary/30 opacity-60 cursor-not-allowed"}`}
              data-testid={`profile-doc-${d.id}`}
            >
              <span className="text-sm font-medium">{d.label}</span>
              {d.url ? <ExternalLink className="w-4 h-4 text-primary" /> : <span className="text-xs text-muted-foreground">not set</span>}
            </a>
          ))}
          {!c.pcsp_url && !c.support_strategy_url && !c.room_board_url && !c.bsp_url && (c.doc_links || []).length === 0 && (
            <p className="text-sm text-muted-foreground text-center py-3">No documents linked yet — ask your admin to add them.</p>
          )}
        </div>
      </Card>

      {(c.training_blocks || []).length > 0 && (
        <Card className="p-4">
          <p className="text-label mb-2">Client-Specific Training</p>
          <div className="space-y-2">
            {c.training_blocks.map((b) => (
              <div key={b.id} className="border border-border rounded-md p-3" data-testid={`training-${b.id}`}>
                <p className="font-display font-semibold text-sm">{b.title}</p>
                <p className="text-sm mt-1 whitespace-pre-wrap">{b.content}</p>
              </div>
            ))}
          </div>
        </Card>
      )}

      {/* v37 — Contractor-editable Property Inventory.
          Host-home providers can now add and archive $50+ tracked items
          directly from their client profile (no admin round-trip), matching
          how the admin profile surfaces the same panel. Backend
          (/api/clients/{cid}/property) was already permissioned on
          `get_current_user` + `assert_client_accessible`, so contractors
          assigned to this client are authorized end-to-end. */}
      <PropertyInventoryPanel clientId={clientId} />
    </div>
  );
}
